import React from 'react';
import { Navigate } from 'react-router-dom';
import { AuthenticationState, useAuthenticationStore } from '@engelvoelkersamericas/common-ui';

import { useBaseRoles } from '@hooks';
import { useProfileStore, useTeamProfileStore } from '@stores';

import { IProtectedRouteProps } from './types';
import { canUseProfilesForOthers, checkUserAccess, IEffectiveUserPermissions, mapEffectiveUserToAuthUser } from './utils';

/**
 * Route-level access control component that redirects to a forbidden page
 * if the user doesn't have the required permissions to access a route.
 *
 * For "other user" profile routes (when isOtherUserProfile is true), this component
 * uses the permission-based approach checking 'can_use_profiles_for_others' permission
 * from the effectiveUser.permissions object.
 *
 * During loading states, children are rendered so components can display their skeletons.
 */
export const ProtectedRoute: React.FC<IProtectedRouteProps> = ({
  children,
  userRequiredRoles,
  otherUserRequiredRoles,
  useDefaultRoles = true,
  context = 'user',
}) => {
  const { contextSelectedUserAccount, isLoadingUserProfile } = useProfileStore();
  const { isOtherUserProfile, baseRoles } = useBaseRoles();
  const { authUserProfile, authStatus } = useAuthenticationStore();
  const { teamProfile, isLoadingTeamProfile } = useTeamProfileStore();

  const effectiveUser = authUserProfile?.effectiveUser;
  const authUser = mapEffectiveUserToAuthUser(effectiveUser);

  const forbidden = (
    <Navigate
      to="/forbidden"
      state={{ error: { status: '403', title: '403', subTitle: 'You are forbidden to access this page.' } }}
      replace
    />
  );

  // Determine if we're in a loading state - render children so they can show skeletons
  const isAuthPending = authStatus !== AuthenticationState.AUTHENTICATED;
  const isLoading = isAuthPending || isLoadingUserProfile || (context === 'team' && isLoadingTeamProfile);

  if (isLoading) {
    return <>{children}</>;
  }

  // After loading complete, check if required data exists
  // Note: contextSelectedUserAccount is initialized as {} which is truthy, so we check for userId
  if (!contextSelectedUserAccount?.userId || !authUser || !baseRoles.length) return null;

  // Team context requires team profile to exist after loading
  if (context === 'team' && !teamProfile) return forbidden;

  // For "other user" profile routes (user context, not team), use permission-based access control
  // This checks for 'can_use_profiles_for_others' permission from effectiveUser.permissions
  if (isOtherUserProfile && context === 'user') {
    const permissions = effectiveUser?.permissions as IEffectiveUserPermissions | undefined;
    const viewedUserShopIds = contextSelectedUserAccount?.shops?.map((shop) => shop.shopId) || [];

    const hasPermission = canUseProfilesForOthers(permissions, viewedUserShopIds);

    if (!hasPermission) {
      return forbidden;
    }

    return <>{children}</>;
  }

  // For own profile or team context, use the existing role-based access checking logic
  const { hasAccess } = checkUserAccess({
    userRequiredRoles,
    otherUserRequiredRoles,
    contextSelectedUserAccount,
    authUser,
    isOtherUserProfile,
    baseRoles,
    useDefaultRoles,
    // Only pass teamProfile for team context - this enables team role checks (e.g., TEAM_ADMIN, TEAM_MEMBER)
    teamProfile: context === 'team' ? teamProfile : undefined,
    context,
  });

  // If the user doesn't have access, redirect to the forbidden page
  if (!hasAccess) {
    return forbidden;
  }

  return <>{children}</>;
};
